'use client';

import { useState } from 'react';
import { Check, Zap } from 'lucide-react';
import Link from 'next/link';
import { platformLink } from '@/lib/config';

export default function PricingSection() {
  const [billingMode, setBillingMode] = useState<'monthly' | 'annual'>('monthly');
  const annual = billingMode === 'annual';

  return (
    <section id="pricing" className="sm:py-24 pt-16 pb-16 relative">
      <div className="sm:px-6 lg:px-8 max-w-7xl mx-auto px-4">
        <div className="mb-12 text-center">
          <span className="inline-flex items-center gap-2 text-sm font-medium text-rose-400 px-3 py-1 rounded-none bg-rose-500/10 border border-rose-500/20">
            Pricing
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-white">
            Start free. Pay as you grow.
          </h2>
          <p className="mt-4 text-base sm:text-lg text-neutral-400 max-w-2xl mx-auto">
            Detection runs on your own infrastructure - you pay for scale, advanced detection and the dashboard. Works with SDK, Cloudflare Worker or Docker.
          </p>
          <p className="mt-3 text-sm text-neutral-500 max-w-2xl mx-auto">
            Not sure which layer you need?{' '}
            <Link href="/best-waf" className="text-neutral-300 underline underline-offset-4 hover:text-white">
              See the best WAF options by use case
            </Link>.
          </p>

          <div className="mt-6 flex justify-center px-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-none bg-gradient-to-r from-rose-500/15 to-purple-500/15 border border-rose-500/30 text-sm flex-wrap justify-center text-center max-w-xl">
              <Zap className="w-4 h-4 text-rose-400 shrink-0" />
              <span className="text-white font-medium">Beta offer:</span>
              <span className="text-neutral-300">50% off your first 3 months. Standard pricing after.</span>
            </div>
          </div>

          <div className="mt-8 flex justify-center">
            <div className="inline-flex bg-neutral-900 p-1 rounded-none border border-white/5">
              <button
                onClick={() => setBillingMode('monthly')}
                className={`px-4 py-2 text-sm font-medium rounded-none transition-all ${
                  !annual ? 'bg-white text-black shadow-sm' : 'text-neutral-400 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingMode('annual')}
                className={`px-4 py-2 text-sm font-medium rounded-none transition-all ${
                  annual ? 'bg-white text-black shadow-sm' : 'text-neutral-400 hover:text-white'
                }`}
              >
                Annual <span className="text-xs text-emerald-600 ml-1 font-bold">-20%</span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Starter */}
          <div className="flex flex-col p-6 rounded-none border border-white/5 bg-neutral-900/20 hover:bg-neutral-900/40 transition-colors">
            <div className="text-lg font-semibold text-white">Starter</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="text-4xl font-bold text-white">$0</span>
              <span className="text-sm text-neutral-400">/mo</span>
            </div>
            <p className="mt-2 text-sm text-neutral-400">For side projects</p>

            <ul className="mt-6 space-y-3 flex-1">
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />1 application</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />Up to 10,000 requests / mo</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />AI threat &amp; OWASP blocking</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />1 free security scan</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />Community support</li>
            </ul>

            <a href={platformLink.register()} className="mt-6 w-full py-3 px-4 bg-white/5 border border-white/10 text-white rounded-none text-sm font-semibold hover:bg-white/10 transition-colors text-center">
              Get Started Free
            </a>
          </div>

          {/* Pro */}
          <div className="flex flex-col p-6 rounded-none border border-rose-500/30 bg-rose-500/5 relative shadow-lg shadow-rose-500/5">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-rose-500 text-white text-xs font-bold rounded-none uppercase tracking-wider">
              Most Popular
            </div>
            <div className="text-lg font-semibold text-white">Pro</div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-4xl font-bold text-white">${annual ? '15.50' : '19.50'}</span>
              <span className="text-sm text-neutral-400">/mo</span>
              <span className="text-xl font-medium text-neutral-500 line-through">${annual ? '31' : '39'}</span>
            </div>
            <p className="mt-1 text-xs text-rose-400 font-medium">Beta: 50% off for first 3 months, then ${annual ? '31' : '39'}/mo</p>

            <ul className="mt-6 space-y-3 flex-1">
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />Up to 2 apps, 500k req / mo</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />AI Verdict &amp; Copilot</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />Global Threat Map</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />Pentest &amp; data leak detection</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />30-day log retention</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />Email support</li>
            </ul>

            <a href={platformLink.register('pro')} className="mt-6 w-full py-3 px-4 bg-white text-black rounded-none text-sm font-semibold hover:bg-neutral-200 transition-colors text-center shadow-lg shadow-rose-500/10">
              Get Pro
            </a>
          </div>

          {/* Business */}
          <div className="flex flex-col p-6 rounded-none border border-white/10 bg-neutral-900/30 hover:bg-neutral-900/50 transition-colors">
            <div className="text-lg font-semibold text-white">Business</div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-4xl font-bold text-white">${annual ? '39.50' : '49.50'}</span>
              <span className="text-sm text-neutral-400">/mo</span>
              <span className="text-xl font-medium text-neutral-500 line-through">${annual ? '79' : '99'}</span>
            </div>
            <p className="mt-1 text-xs text-rose-400 font-medium">Beta: 50% off for first 3 months, then ${annual ? '79' : '99'}/mo</p>

            <ul className="mt-6 space-y-3 flex-1">
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-neutral-300 shrink-0 mt-0.5" />Everything in Pro</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-neutral-300 shrink-0 mt-0.5" />Up to 5 apps, unlimited req / mo</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-neutral-300 shrink-0 mt-0.5" />Team seats &amp; Google SSO</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-neutral-300 shrink-0 mt-0.5" />On-demand scans &amp; Slack alerts</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-neutral-300 shrink-0 mt-0.5" />Priority support</li>
            </ul>

            <a href={platformLink.register('business')} className="mt-6 w-full py-3 px-4 bg-white/5 border border-white/10 text-white rounded-none text-sm font-semibold hover:bg-white/10 transition-colors text-center">
              Get Business
            </a>
          </div>

          {/* Enterprise */}
          <div className="flex flex-col p-6 rounded-none border border-white/5 bg-neutral-900/20 hover:bg-neutral-900/40 transition-colors">
            <div className="text-lg font-semibold text-white">Enterprise</div>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="text-4xl font-bold text-white">Custom</span>
            </div>
            <p className="mt-2 text-sm text-neutral-400">Self-host &amp; regulated</p>

            <ul className="mt-6 space-y-3 flex-1">
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-neutral-500 shrink-0 mt-0.5" />On-prem / self-hosted</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-neutral-500 shrink-0 mt-0.5" />Unlimited apps &amp; requests</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-neutral-500 shrink-0 mt-0.5" />SAML SSO, SIEM export</li>
              <li className="flex items-start gap-2.5 text-sm text-neutral-300"><Check className="w-5 h-5 text-neutral-500 shrink-0 mt-0.5" />Custom SLA &amp; dedicated support</li>
            </ul>

            <a href="mailto:hello@silkerai.com?subject=Silker%20Enterprise" className="mt-6 w-full py-3 px-4 bg-white/5 border border-white/10 text-white rounded-none text-sm font-semibold hover:bg-white/10 transition-colors text-center">
              Contact Sales
            </a>
          </div>
        </div>

        <p className="text-center text-xs text-neutral-500 mt-8">
          Prices in USD. Cancel anytime. Beta: 50% off your first 3 months, then standard pricing.
        </p>
      </div>
    </section>
  );
}
